import { cn } from '@/lib/utils'
import { sanitizeImageUrl } from '@/utils/url'
import { ImageUrlService } from '@/services/imageUrlService'
import React, { useEffect, useRef, useState } from 'react'

/**
 * OptimizedImage - Componente de imagem otimizado (Fase 4)
 * 
 * Features:
 * - Lazy loading com IntersectionObserver
 * - Placeholder blur-up effect
 * - Fallback automático em caso de erro
 * - Responsive images com srcset
 * - Preload para imagens prioritárias
 * - Migração automática de URLs legadas
 * - Cache inteligente
 */

interface OptimizedImageProps {
  src: string
  alt: string
  className?: string
  width?: number
  height?: number
  placeholder?: string
  fallback?: string
  priority?: boolean
  sizes?: string
  responsive?: boolean
  responsiveSizes?: number[]
  onLoad?: () => void
  onError?: () => void
  // Fase 4: Novas props
  blurDataURL?: string  // Data URL para blur placeholder
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down'
  rootMargin?: string   // Margem para IntersectionObserver
}

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className,
  width,
  height,
  placeholder = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY2NzM4NyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlbTwvdGV4dD48L3N2Zz4=',
  fallback = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZWY0NDQ0Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iI2ZmZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkVycm88L3RleHQ+PC9zdmc+',
  priority = false,
  sizes = '100vw',
  responsive = false,
  responsiveSizes = [320, 640, 768, 1024, 1280],
  onLoad,
  onError,
  // Fase 4: Novas props
  blurDataURL,
  objectFit = 'cover',
  rootMargin = '200px', // Aumentado de 100px para 200px (Fase 4)
}) => {
  const [imageSrc, setImageSrc] = useState(blurDataURL || placeholder)
  const [isLoaded, setIsLoaded] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [isInView, setIsInView] = useState(priority)
  const imgRef = useRef<HTMLImageElement | null>(null)

  // Fase 4: IntersectionObserver otimizado com rootMargin configurável
  useEffect(() => {
    if (priority) return
    if (!imgRef.current) return
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { 
        rootMargin, // Configurável (Fase 4)
        threshold: 0.01 // Trigger quando 1% da imagem estiver visível
      }
    )
    
    observer.observe(imgRef.current)
    return () => observer.disconnect()
  }, [priority, rootMargin])

  useEffect(() => {
    if (!isInView) return

    // Migrar URL legada para Supabase Storage
    const migratedSrc = ImageUrlService.migrateLegacyUrl(src)
    const optimizedSrc = sanitizeImageUrl(migratedSrc)

    const img = new Image()
    
    img.onload = () => {
      setImageSrc(optimizedSrc)
      setIsLoaded(true)
      setHasError(false)
      onLoad?.()
    }
    
    img.onerror = () => {
      setHasError(true)
      setIsLoaded(false)
      // Tentar fallback
      if (src !== fallback) {
        setImageSrc(fallback)
      }
      onError?.()
    }
    
    img.src = optimizedSrc
  }, [src, isInView, fallback, onLoad, onError, priority])

  return (
    <img
      ref={imgRef}
      src={imageSrc}
      alt={alt}
      className={cn(
        // Fase 4: Transições melhoradas com blur effect
        'transition-all duration-500 ease-out',
        // Blur effect enquanto carrega (apenas se tiver blurDataURL)
        blurDataURL && !isLoaded && !hasError && 'blur-sm scale-105',
        // Fade in quando carregar
        isLoaded ? 'opacity-100 blur-0 scale-100' : 'opacity-0',
        // Mostrar placeholder/erro imediatamente
        (imageSrc === placeholder || hasError) && 'opacity-100 blur-0 scale-100',
        className
      )}
      style={{
        objectFit,
        ...(width && height ? { aspectRatio: `${width} / ${height}` } : {})
      }}
      width={width}
      height={height}
      sizes={sizes}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      // Fase 4: Melhor acessibilidade
      aria-label={alt}
      role="img"
    />
  )
}

export default OptimizedImage 